import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { X, Send, MessageSquare, Clock, User } from 'lucide-react';
import commissionMessagesService from '../../services/commissionMessagesService';
import travelieTheme from '../../styles/travelieTheme';
import { useResponsive } from '../../hooks/useResponsive';

// Phase 7: Migrated to TravelieTheme - June 16, 2026

const CommissionMessagingModal = ({ commission, onClose, onMessageSent, onCommissionUpdate }) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();

  // Helper function for button styling
  const getButtonStyle = (type, disabled = false) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      opacity: disabled ? 0.5 : 1
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary }
    };

    return styles[type] || styles.primary;
  };

  // Helper function for status badge styling
  const getStatusBadgeStyle = (status) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
      fontSize: travelieTheme.typography.fontSize.xs,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      display: 'inline-block',
      textTransform: 'capitalize'
    };

    const statusStyles = {
      approved: { ...baseStyle, background: '#DBEAFE', color: '#1E40AF' },
      paid: { ...baseStyle, background: '#DCFCE7', color: '#166534' },
      pending: { ...baseStyle, background: '#FEF3C7', color: '#92400E' },
      denied: { ...baseStyle, background: '#FEE2E2', color: '#991B1B' },
      on_hold: { ...baseStyle, background: '#F3E8FF', color: '#6B21A8' },
      needs_reply: { ...baseStyle, background: '#FED7AA', color: '#9A3412' },
      needs_agent_reply: { ...baseStyle, background: '#FED7AA', color: '#9A3412' },
      needs_admin_reply: { ...baseStyle, background: '#FECACA', color: '#7F1D1D' }
    };

    return statusStyles[status] || statusStyles.pending;
  };
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Date formatting function to avoid timezone issues
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    // Use string splitting to avoid timezone conversion issues
    const datePart = dateString.split('T')[0]; // Get YYYY-MM-DD part
    const [year, month, day] = datePart.split('-');
    const date = new Date(year, month - 1, day); // Create date in local timezone
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    if (commission?.id) {
      loadMessages();
      markMessagesAsRead();
    }
  }, [commission?.id]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await commissionMessagesService.getMessages(commission.id);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      alert('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async () => {
    try {
      await commissionMessagesService.markAsRead(commission.id);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    
    try {
      const messageText = newMessage.trim();
      await commissionMessagesService.sendAgentReply(commission.id, messageText);
      
      // Refresh messages after sending
      await loadMessages();
      
      setNewMessage('');
      
      // Auto-scroll to bottom
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: travelieTheme.spacing.md }} onClick={onClose}>
      <div style={{ background: "#FFFFFF", borderRadius: travelieTheme.components.card.borderRadius, maxWidth: "800px", width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: travelieTheme.spacing.lg, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2>Commission Messages</h2>
          <button style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: travelieTheme.colors.text.secondary, padding: 0, width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
            <svg style={{ width: "24px", height: "24px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Commission Summary */}
        <div style={{ padding: travelieTheme.spacing.md, background: "#F9FAFB", borderBottom: `1px solid ${travelieTheme.colors.neutral.border}` }}>
          <h3>Commission Details</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: travelieTheme.spacing.md }}>
            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
              <span style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Reservation #:</span>
              <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary, fontWeight: travelieTheme.typography.fontWeight.semibold }}>{commission.reservation_number}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
              <span style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Guest:</span>
              <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary, fontWeight: travelieTheme.typography.fontWeight.semibold }}>{commission.guest_first_name} {commission.guest_last_name}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
              <span style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Supplier:</span>
              <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary, fontWeight: travelieTheme.typography.fontWeight.semibold }}>{commission.supplier}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
              <span style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Travel Date:</span>
              <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary, fontWeight: travelieTheme.typography.fontWeight.semibold }}>{formatDateForDisplay(commission.travel_date)}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
              <span style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Gross Commission:</span>
              <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary, fontWeight: travelieTheme.typography.fontWeight.semibold }}>{formatCurrency(commission.gross_commission)}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
              <span style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Agent Commission:</span>
              <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary, fontWeight: travelieTheme.typography.fontWeight.semibold }}>{formatCurrency(commission.agent_commission)}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
              <span style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Status:</span>
              <span style={getStatusBadgeStyle(commission.status)}>
                {commission.status.charAt(0).toUpperCase() + commission.status.slice(1).replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>

        {/* Messages Section */}
        <div style={{ flex: 1, overflow: "auto", padding: travelieTheme.spacing.lg, background: "#FFFFFF" }}>
          <h3>Message History</h3>
          
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: travelieTheme.spacing.xl }}>
              <div style={{ width: "40px", height: "40px", border: "4px solid #E5E7EB", borderTop: "4px solid " + travelieTheme.colors.primary.main, borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
              <p>Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: "center", padding: travelieTheme.spacing.xl, color: travelieTheme.colors.text.secondary }}>
              <p>No messages yet.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.md }}>
              {messages.map((message) => (
                <div 
                  key={message.id} 
                  style={{ 
                    padding: travelieTheme.spacing.md, 
                    background: message.sender_type === 'agent' ? '#DBEAFE' : '#F9FAFB',
                    borderLeft: message.sender_type === 'agent' ? `4px solid ${travelieTheme.colors.primary.main}` : '4px solid #E5E7EB',
                    borderRadius: travelieTheme.components.card.borderRadius
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: travelieTheme.spacing.xs }}>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>
                      {message.sender_type === 'agent' ? 'You' : message.sender_name}
                    </span>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary }}>{formatDate(message.created_at)}</span>
                  </div>
                  <div style={{ padding: travelieTheme.spacing.md, background: "#F9FAFB", borderRadius: travelieTheme.components.card.borderRadius, fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary, whiteSpace: "pre-wrap" }}>
                    {message.message}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Reply Section - Always Available */}
        <div style={{ padding: travelieTheme.spacing.lg, borderTop: `1px solid ${travelieTheme.colors.neutral.border}`, background: "#FFFFFF" }}>
          <h3>Send Message</h3>
          <div style={{ padding: travelieTheme.spacing.md, background: "#FEF3C7", border: "1px solid #FDE047", borderRadius: travelieTheme.components.card.borderRadius, marginBottom: travelieTheme.spacing.md }}>
            <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: "#92400E", fontWeight: travelieTheme.typography.fontWeight.medium }}>Commission Status:</span>
            <span style={getStatusBadgeStyle(commission.status)}>
              {commission.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <form onSubmit={(e) => e.preventDefault()}>
            <div style={{ display: "flex", gap: travelieTheme.spacing.sm, marginBottom: travelieTheme.spacing.sm }}>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message about this commission..."
                style={{ width: "100%", padding: travelieTheme.spacing.sm, border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, resize: "none", outline: "none", fontSize: travelieTheme.typography.fontSize.sm }}
                rows={3}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: travelieTheme.spacing.sm }}>
              <button 
                type="button" 
                onClick={onClose}
                style={getButtonStyle("secondary")}
                disabled={sending}
              >
                Close
              </button>
              <button 
                type="submit" 
                style={getButtonStyle("primary", sending)}
                disabled={sending || !newMessage.trim()}
                onClick={handleSendMessage}
              >
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommissionMessagingModal;
